import { useRef, useState } from "react";
import { Input, QRCode } from "antd";
import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  PAM_MFA_ERROR_CODES,
  getPamMfaErrorCode,
  getPamMfaErrorMessage,
} from "../../../utils/auth/pamMfa";
import { BRAND_ICONS_URLS } from "../../../utils/Constants";
import "./stylesAuthenticatorEnrollment.css";

function AuthenticatorEnrollment({
  secret,
  otpauthUri,
  onVerify,
  onCancel,
  onExpired,
}) {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const submissionInFlight = useRef(false);

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopyMessage(AppStrings.PAM_2FA_KEY_COPIED);
    } catch {
      setCopyMessage(AppStrings.PAM_2FA_COPY_FAILED);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      code.length !== 6 ||
      isVerifying ||
      isRateLimited ||
      submissionInFlight.current
    ) {
      return;
    }

    submissionInFlight.current = true;
    setIsVerifying(true);
    setErrorMessage("");
    try {
      await onVerify(code);
    } catch (error) {
      const errorCode = getPamMfaErrorCode(error);
      const message = getPamMfaErrorMessage(error);
      if (errorCode === PAM_MFA_ERROR_CODES.ENROLLMENT_TICKET_EXPIRED) {
        onExpired?.(message);
        return;
      }
      if (errorCode === PAM_MFA_ERROR_CODES.RATE_LIMITED) {
        setIsRateLimited(true);
      }
      setErrorMessage(message);
    } finally {
      submissionInFlight.current = false;
      setIsVerifying(false);
    }
  };

  return (
    <form
      className="authenticator-enrollment authenticator-enrollment--setup"
      aria-label={AppStrings.PAM_2FA_ENROLLMENT_TITLE}
      onSubmit={handleSubmit}
    >
      <h1 className="authenticator-enrollment-title">
        {AppStrings.PAM_2FA_ENROLLMENT_TITLE}
      </h1>
      <p className="authenticator-enrollment-description">
        {AppStrings.PAM_2FA_ENROLLMENT_DESCRIPTION}
      </p>

      <section
        className="authenticator-enrollment-qr"
        aria-label={AppStrings.PAM_2FA_SCAN_QR_CODE}
      >
        <QRCode
          value={otpauthUri}
          size={180}
          errorLevel="H"
          bordered={false}
          style={{ width: 180, height: 180 }}
          icon={
            process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
            BRAND_ICONS_URLS.companylogo
          }
          iconSize={36}
        />
      </section>

      <div className="authenticator-enrollment-secret">
        <span>{AppStrings.PAM_2FA_MANUAL_KEY_LABEL}</span>
        <code data-testid="totp-secret">{secret}</code>
        <button
          type="button"
          className="authenticator-enrollment-copy"
          onClick={handleCopySecret}
        >
          {AppStrings.PAM_2FA_COPY_KEY}
        </button>
        {copyMessage && (
          <span className="authenticator-enrollment-copy-status" role="status">
            {copyMessage}
          </span>
        )}
      </div>

      <label className="authenticator-enrollment-code-label">
        {AppStrings.PAM_2FA_CODE_LABEL}
      </label>
      <Input.OTP
        className="authenticator-enrollment-code"
        data-testid="totp-code-input"
        length={6}
        value={code}
        type="tel"
        inputMode="numeric"
        size="large"
        disabled={isVerifying || isRateLimited}
        onChange={(value) => {
          setCode(value.replace(/\D/g, ""));
          setErrorMessage("");
        }}
        formatter={(value) => value.replace(/\D/g, "")}
      />

      {errorMessage && (
        <p className="authenticator-enrollment-error" role="alert">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        className="loginButton authenticator-enrollment-submit"
        disabled={code.length !== 6 || isVerifying || isRateLimited}
      >
        <span className="loginButtonText">
          {isVerifying
            ? AppStrings.PAM_2FA_VERIFYING
            : AppStrings.PAM_2FA_VERIFY}
        </span>
      </button>
      <button
        type="button"
        className="authenticator-enrollment-cancel"
        onClick={onCancel}
        disabled={isVerifying}
      >
        {AppStrings.PAM_2FA_BACK_TO_LOGIN}
      </button>
    </form>
  );
}

export default AuthenticatorEnrollment;
